/*
 * Purpose: Canonical block catalog — primitives the AutoBlocks engine can assemble
 * Last modified: 2026-06-25 | Agent
 * Completeness: 85
 */
import type {
  ActionType,
  AgentType,
  BrandedType,
  DroneType,
  GoalType,
  StartType,
  ToolType,
  TriggerType,
  TubeType,
} from '@/stores/workflowStore';
import { BRANDED_BLOCKS } from '@/lib/brandedBlocks';
import { getBlockTypeLabel, type BlockCategory } from '@/lib/blockTypes';

export type { BlockCategory };

export interface BlockDefinition {
  id: string;
  category: BlockCategory;
  subtype: StartType | TriggerType | ToolType | ActionType | GoalType | AgentType | DroneType | TubeType | BrandedType;
  label: string;
  description: string;
  capabilities: string[];
  inputSchema?: string;
  outputSchema?: string;
  tags: string[];
}

export const BLOCK_REGISTRY: BlockDefinition[] = [
  {
    id: 'start.mission',
    category: 'start',
    subtype: 'mission',
    label: getBlockTypeLabel('start', 'mission'),
    description: 'Required Mission entry — execution begins here every run.',
    capabilities: ['mission_entry', 'run_anchor'],
    tags: ['start', 'mission', 'required'],
  },
  {
    id: 'goal.outcome',
    category: 'goal',
    subtype: 'outcome',
    label: getBlockTypeLabel('goal', 'outcome'),
    description: 'Identified outcome — what this Mission should achieve (from Composer or manual).',
    capabilities: ['outcome_anchor', 'intent', 'success_criteria'],
    tags: ['goal', 'outcome', 'intent'],
  },
  {
    id: 'agent.assistant',
    category: 'agent',
    subtype: 'assistant',
    label: getBlockTypeLabel('agent', 'assistant'),
    description:
      'Swiss-army agent — reasons and plans in this Mission once contextual configuration defines its brief.',
    capabilities: ['reasoning', 'replan', 'delegation', 'ai'],
    tags: ['agent', 'ai', 'autonomous'],
  },
  {
    id: 'drone.remote',
    category: 'drone',
    subtype: 'remote',
    label: getBlockTypeLabel('drone', 'remote'),
    description:
      'Swiss-army drone — executes a delegated task remotely once contextual configuration defines the mission.',
    capabilities: ['remote_execution', 'delegation', 'worker'],
    tags: ['drone', 'remote', 'worker'],
  },
  {
    id: 'tube.journey',
    category: 'tube',
    subtype: 'journey',
    label: getBlockTypeLabel('tube', 'journey'),
    description:
      'Journey Tube — Director leaves an open path to the outcome when natural pacing is best; blocks attach along the tube.',
    capabilities: ['journey', 'deferred_wiring', 'natural_pacing'],
    tags: ['journey', 'tube', 'director'],
  },
  ...BRANDED_BLOCKS.map((b) => ({
    id: b.id,
    category: 'branded' as const,
    subtype: b.subtype,
    label: b.label,
    description: b.description,
    capabilities: b.capabilities,
    tags: ['branded', b.brand.toLowerCase()],
  })),
  {
    id: 'trigger.manual',
    category: 'trigger',
    subtype: 'manual',
    label: getBlockTypeLabel('trigger', 'manual'),
    description: 'Start on demand — good for testing and human-initiated flows.',
    capabilities: ['human_start', 'testing'],
    tags: ['start', 'test'],
  },
  {
    id: 'trigger.gmail_inbound',
    category: 'trigger',
    subtype: 'gmail_inbound',
    label: getBlockTypeLabel('trigger', 'gmail_inbound'),
    description: 'Poll latest parsed email from local webhook receiver (live) or fixture (dry run).',
    capabilities: ['inbound_events', 'email', 'gmail'],
    tags: ['email', 'gmail', 'start'],
  },
  {
    id: 'trigger.webhook',
    category: 'trigger',
    subtype: 'webhook',
    label: getBlockTypeLabel('trigger', 'webhook'),
    description: 'Receive events from email parsers, forms, or external services.',
    capabilities: ['inbound_events', 'email_bridge', 'integrations'],
    tags: ['email', 'api', 'start'],
  },
  {
    id: 'trigger.timer',
    category: 'trigger',
    subtype: 'timer',
    label: getBlockTypeLabel('trigger', 'timer'),
    description: 'Run on a schedule or interval.',
    capabilities: ['scheduling', 'reminders'],
    tags: ['schedule', 'cron'],
  },
  {
    id: 'tool.transform',
    category: 'tool',
    subtype: 'transform',
    label: getBlockTypeLabel('tool', 'transform'),
    description: 'Parse, map, score, or reshape data between steps.',
    capabilities: ['parsing', 'mapping', 'scoring', 'custom_logic'],
    tags: ['data', 'logic'],
  },
  {
    id: 'tool.conditional',
    category: 'tool',
    subtype: 'conditional',
    label: getBlockTypeLabel('tool', 'conditional'),
    description: 'Continue only when a condition is met (relevance, validation).',
    capabilities: ['branching', 'filtering'],
    tags: ['if', 'filter'],
  },
  {
    id: 'tool.http',
    category: 'tool',
    subtype: 'http',
    label: getBlockTypeLabel('tool', 'http'),
    description: 'Call external APIs — AI, CRM, job boards, storage.',
    capabilities: ['api_calls', 'ai', 'integrations'],
    tags: ['api', 'http'],
  },
  {
    id: 'tool.delay',
    category: 'tool',
    subtype: 'delay',
    label: getBlockTypeLabel('tool', 'delay'),
    description: 'Wait before the next step (rate limits, follow-up timing).',
    capabilities: ['timing'],
    tags: ['wait'],
  },
  {
    id: 'action.storage',
    category: 'action',
    subtype: 'storage',
    label: getBlockTypeLabel('action', 'storage'),
    description: 'Persist records to local or remote storage (tracker, logs).',
    capabilities: ['persistence', 'tracker'],
    tags: ['save', 'database'],
  },
  {
    id: 'action.notification',
    category: 'action',
    subtype: 'notification',
    label: getBlockTypeLabel('action', 'notification'),
    description: 'Alert the user when something completes or needs attention.',
    capabilities: ['alerts', 'user_feedback'],
    tags: ['notify', 'toast'],
  },
  {
    id: 'action.log',
    category: 'action',
    subtype: 'log',
    label: getBlockTypeLabel('action', 'log'),
    description: 'Record step output for debugging and audit.',
    capabilities: ['observability'],
    tags: ['debug', 'audit'],
  },
  {
    id: 'action.webhook',
    category: 'action',
    subtype: 'webhook',
    label: getBlockTypeLabel('action', 'webhook'),
    description: 'POST results to another system (Slack, Sheets, CRM).',
    capabilities: ['outbound_events', 'integrations'],
    tags: ['api', 'post'],
  },
];

export function getBlock(id: string): BlockDefinition | undefined {
  return BLOCK_REGISTRY.find((b) => b.id === id);
}

export function blocksByTag(tag: string): BlockDefinition[] {
  return BLOCK_REGISTRY.filter((b) => b.tags.includes(tag));
}
